import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  PAYMENT_PREVIEW_ACH_AUTHORIZATION,
  PAYMENT_PREVIEW_ACH_TERMINATION,
  PAYMENT_PREVIEW_FEE_NOTE,
  PAYMENT_PREVIEW_TERMS,
} from "../../../shared/paymentInformationPreview";
import {
  PAYMENT_TEST_ACCOUNT,
  PAYMENT_TEST_CARD,
  PAYMENT_TEST_ROUTING,
  paymentInformationTestSchema,
  type PaymentInformationTest,
} from "../../../shared/paymentInformationTest";
import "./payment-information-preview.css";

type Method = "credit-card" | "ach";
type TestConfig = { emailAvailable: boolean; adminEmailAllowed: boolean };
type FormValues = {
  companyName: string;
  cardholder: string;
  cardNumber: string;
  expiration: string;
  cvv: string;
  accountType: "Checking" | "Savings";
  accountHolder: string;
  accountNumber: string;
  routingNumber: string;
  bankName: string;
  bankAddress: string;
  bankCityStateZip: string;
  signerName: string;
  title: string;
  signature: string;
  date: string;
};
type FieldErrors = Record<string, string>;
type Attempt = { snapshot: string; reference: string };

const emptyValues: FormValues = {
  companyName: "", cardholder: "", cardNumber: "", expiration: "", cvv: "",
  accountType: "Checking", accountHolder: "", accountNumber: "", routingNumber: "",
  bankName: "", bankAddress: "", bankCityStateZip: "", signerName: "", title: "",
  signature: "", date: new Date().toLocaleDateString("en-CA"),
};

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function makeReference() {
  return crypto.randomUUID();
}

function PaymentField({
  name, label, value, onChange, error, hint, wide, type = "text", maxLength,
  disabled = false, inputMode, autoComplete = "off",
}: {
  name: keyof FormValues;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  wide?: boolean;
  type?: string;
  maxLength?: number;
  disabled?: boolean;
  inputMode?: "text" | "numeric" | "decimal";
  autoComplete?: string;
}) {
  const id = `payment-${name}`;
  return <div className={`payment-preview-field${wide ? " is-wide" : ""}`}>
    <label className="payment-preview-label" htmlFor={id}>{label}</label>
    <input
      id={id}
      name={name}
      className={`forms-control payment-preview-input${error ? " is-invalid" : ""}`}
      type={type}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      aria-invalid={!!error}
      aria-describedby={[hint ? `${id}-hint` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined}
      maxLength={maxLength}
      inputMode={inputMode}
      autoComplete={autoComplete}
      disabled={disabled}
    />
    {hint && <p id={`${id}-hint`} className="forms-hint payment-preview-hint">{hint}</p>}
    {error && <p id={`${id}-error`} className="forms-error">{error}</p>}
  </div>;
}

export default function PaymentInformationPreview() {
  const [method, setMethod] = useState<Method>("credit-card");
  const [values, setValues] = useState<FormValues>(() => ({ ...emptyValues, date: today() }));
  const [terms, setTerms] = useState("");
  const [consent, setConsent] = useState(false);
  const [syntheticOnly, setSyntheticOnly] = useState(false);
  const [feeAcknowledgment, setFeeAcknowledgment] = useState(false);
  const [emailCopy, setEmailCopy] = useState(false);
  const [config, setConfig] = useState<TestConfig | null>(null);
  const [configError, setConfigError] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ reference: string; emailStatus: string; url: string } | null>(null);
  const attempt = useRef<Attempt | null>(null);
  const downloadUrl = useRef("");
  const emailEligible = !!config?.emailAvailable && !!config?.adminEmailAllowed;

  const loadConfig = async () => {
    setConfigError("");
    try {
      const response = await fetch("/api/forms/payment-information/test-config", {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("We could not check test email eligibility.");
      const result = await response.json() as TestConfig;
      setConfig({ emailAvailable: !!result.emailAvailable, adminEmailAllowed: !!result.adminEmailAllowed });
    } catch {
      setConfigError("Test email eligibility could not be checked. Retry before submitting.");
    }
  };

  useEffect(() => {
    void loadConfig();
    return () => {
      if (downloadUrl.current) URL.revokeObjectURL(downloadUrl.current);
    };
  }, []);

  const updateField = (name: keyof FormValues, value: string) => {
    setValues(previous => ({ ...previous, [name]: value }));
    setErrors(previous => {
      const next = { ...previous };
      delete next[name];
      if (name === "signerName") delete next.signature;
      return next;
    });
    setMessage("");
    setSuccess(null);
  };

  const selectMethod = (nextMethod: Method) => {
    if (nextMethod === method || submitting) return;
    setMethod(nextMethod);
    setValues(previous => nextMethod === "ach"
      ? { ...previous, cardholder: "", cardNumber: "", expiration: "", cvv: "" }
      : { ...previous, accountType: "Checking", accountHolder: "", accountNumber: "", routingNumber: "", bankName: "", bankAddress: "", bankCityStateZip: "" });
    setErrors({});
    setFeeAcknowledgment(false);
    setMessage("");
    setSuccess(null);
  };

  const fillExample = () => {
    if (submitting) return;
    setValues(previous => method === "credit-card" ? {
      ...previous,
      companyName: "Example Healthcare LLC",
      cardholder: "Taylor Example",
      cardNumber: PAYMENT_TEST_CARD,
      expiration: "12/2030",
      cvv: "000",
      signerName: "Taylor Example",
      title: "Practice Manager",
      signature: "Taylor Example",
    } : {
      ...previous,
      companyName: "Example Healthcare LLC",
      accountType: "Checking",
      accountHolder: "Example Healthcare LLC",
      accountNumber: PAYMENT_TEST_ACCOUNT,
      routingNumber: PAYMENT_TEST_ROUTING,
      bankName: "Example Bank",
      bankAddress: "100 Example Street",
      bankCityStateZip: "Boise, ID 83709",
      signerName: "Taylor Example",
      title: "Practice Manager",
      signature: "Taylor Example",
    });
    setTerms("Due On Receipt");
    setConsent(true);
    setSyntheticOnly(true);
    setFeeAcknowledgment(method === "credit-card");
    setErrors({});
    setMessage("");
    setSuccess(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setMessage("");
    setSuccess(null);
    const draft: Record<string, unknown> = {
      reference: "00000000-0000-4000-8000-000000000000",
      method: method === "credit-card" ? "credit-card" : "ach",
      companyName: values.companyName,
      signerName: values.signerName,
      title: values.title,
      signature: values.signature,
      date: values.date,
      terms,
      consent,
      syntheticOnly,
      emailCopy: emailCopy && emailEligible,
      ...(method === "credit-card" ? {
        cardholder: values.cardholder,
        cardNumber: values.cardNumber,
        expiration: values.expiration,
        cvv: values.cvv,
        feeAcknowledgment,
      } : {
        accountType: values.accountType,
        accountHolder: values.accountHolder,
        accountNumber: values.accountNumber,
        routingNumber: values.routingNumber,
        bankName: values.bankName,
        bankAddress: values.bankAddress,
        bankCityStateZip: values.bankCityStateZip,
      }),
    };
    const preliminary = paymentInformationTestSchema.safeParse(draft);
    const nextErrors: FieldErrors = {};
    if (!preliminary.success) {
      for (const issue of preliminary.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      setMessage("Review the highlighted fields and acknowledgments.");
      document.querySelector(".forms-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (emailCopy && !emailEligible) {
      setEmailCopy(false);
      setErrors({ emailCopy: "Emailing is not available for this session. Sign in as an administrator with test email access." });
      setMessage("Review the test email option.");
      return;
    }

    const snapshotPayload = { ...preliminary.data, reference: undefined };
    const snapshot = JSON.stringify(snapshotPayload);
    if (!attempt.current || attempt.current.snapshot !== snapshot) {
      attempt.current = { snapshot, reference: makeReference() };
    }
    const payload = { ...preliminary.data, reference: attempt.current.reference } as PaymentInformationTest;
    setErrors({});
    setSubmitting(true);
    try {
      const response = await fetch("/api/forms/payment-information/test-submissions", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let detail = "";
        try {
          const result = await response.json() as { error?: string };
          detail = result.error || "";
        } catch { /* Keep the local answers and show the generic network response. */ }
        throw new Error(detail || "We could not generate the test PDF. Your answers are still here; retry when ready.");
      }
      const pdf = await response.blob();
      const reference = response.headers.get("X-Test-Reference") || payload.reference;
      const emailStatus = response.headers.get("X-Test-Email-Status") || "not-requested";
      const url = URL.createObjectURL(pdf);
      if (downloadUrl.current) URL.revokeObjectURL(downloadUrl.current);
      downloadUrl.current = url;
      setSuccess({ reference, emailStatus, url });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "We could not generate the test PDF. Your answers are still here; retry when ready.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = (name: keyof FormValues, label: string, options: Partial<Parameters<typeof PaymentField>[0]> = {}) =>
    <PaymentField key={name} name={name} label={label} value={values[name]} onChange={value => updateField(name, value)} error={errors[name]} disabled={submitting} {...options} />;

  return <main className="forms-shell payment-preview">
    <div className="forms-topline"><span>Premier Pharmaceuticals</span><span>Practice onboarding</span></div>
    <header className="forms-form-head">
      <Link to="/forms" className="forms-back">← All forms</Link>
      <p className="forms-eyebrow">Forms portal · Pre-production test</p>
      <h1>Payment Information</h1>
      <p>Editable test paperwork for licensed practices. No real payment information is accepted.</p>
    </header>

    <aside className="payment-preview-warning" role="note">
      <strong>Synthetic test data only. Never enter real card, bank, or other financial information.</strong>
      <span>This test creates a PDF only. It does not process payment or create a real authorization.</span>
    </aside>
    <aside className="payment-preview-retention" aria-label="Test data retention notice">
      <strong>Pre-production test handling</strong>
      <p>Answers are not saved in this browser. Tests do not create a portal submission or admin repository record, and no retention countdown exists for these tests. Delivery callbacks, processing recovery, and the two-hour deletion policy are not tested here.</p>
    </aside>
    {configError && <div className="forms-alert forms-alert-warning" role="status">{configError} <button type="button" onClick={() => void loadConfig()}>Check again</button></div>}
    {!config && !configError && <div className="forms-config-loading" role="status"><span /> Checking test email eligibility</div>}

    <section className="payment-preview-method" aria-label="Payment method">
      <div><h2>Choose Your Payment Method</h2></div>
      <div className="payment-preview-tabs" role="group" aria-label="Payment method">
        <button type="button" aria-pressed={method === "credit-card"} className={method === "credit-card" ? "is-active" : ""} onClick={() => selectMethod("credit-card")} disabled={submitting}>Credit Card</button>
        <button type="button" aria-pressed={method === "ach"} className={method === "ach" ? "is-active" : ""} onClick={() => selectMethod("ach")} disabled={submitting}>ACH</button>
      </div>
    </section>

    {success ? <section className="payment-preview-success" role="status" aria-live="polite">
      <p className="forms-eyebrow">Test PDF ready</p>
      <h2>Your synthetic test is complete.</h2>
      <p>Reference: <strong>{success.reference}</strong></p>
      <a className="forms-button" href={success.url} download={`payment-information-test-${success.reference}.pdf`}>Download test PDF</a>
      {success.emailStatus === "accepted"
        ? <p className="payment-preview-success-note">The test email request was accepted. Accepted does not mean delivered. This is not a payment, authorization, portal submission, or admin repository record; no retention countdown exists for this test. Delivery callbacks and processing recovery were not tested.</p>
        : <p className="payment-preview-success-note">No test email was requested. This PDF is not a payment, authorization, portal submission, or admin repository record; no retention countdown exists for this test. Delivery callbacks and processing recovery were not tested.</p>}
      <button type="button" className="forms-button forms-button-secondary" onClick={() => { setSuccess(null); attempt.current = null; }}>Edit answers</button>
    </section> : <form className="payment-preview-sheet" onSubmit={handleSubmit} noValidate>
      <div className="payment-preview-sheet-head">
        <div><p className="payment-preview-kicker">Premier Pharmaceuticals, LLC</p><h2>Payment authorization</h2></div>
        <span className="payment-preview-stamp">SYNTHETIC TEST · NO PAYMENT</span>
      </div>
      <div className="payment-preview-form-tools">
        <p>Fields are blank to start. The example uses only the published dummy test values.</p>
        <button className="forms-text-button" type="button" onClick={fillExample} disabled={submitting}>Fill test example</button>
      </div>

      <fieldset className="forms-section">
        <legend><span>01</span>Business identity</legend>
        <div className="payment-preview-grid">
          {field("companyName", "Legal company name", { wide: true, maxLength: 65 })}
        </div>
      </fieldset>

      {method === "credit-card" ? <>
        <fieldset className="forms-section">
          <legend><span>02</span>Credit card details</legend>
          <div className="payment-preview-grid">
            {field("cardholder", "Name on card", { maxLength: 65 })}
            {field("cardNumber", "Card number", { wide: true, hint: `Test accepts ${PAYMENT_TEST_CARD} only. Never enter a real card number.`, inputMode: "numeric", maxLength: 19 })}
            {field("expiration", "Expiration · MM/YYYY", { hint: "Use MM/YYYY.", maxLength: 7 })}
            {field("cvv", "CVV", { hint: "Dummy test value 000 only. Never enter a real CVV.", inputMode: "numeric", maxLength: 3 })}
          </div>
        </fieldset>
      </> : <>
        <fieldset className="forms-section">
          <legend><span>02</span>Bank account details</legend>
          <div className="payment-preview-account-types">
            <span className="payment-preview-label">Account type</span>
            <div className="payment-preview-choices">
              {(["Checking", "Savings"] as const).map(option => <label className={`payment-preview-choice${values.accountType === option ? " is-selected" : ""}`} key={option}>
                <input type="radio" name="accountType" value={option} checked={values.accountType === option} disabled={submitting} onChange={() => { setValues(previous => ({ ...previous, accountType: option })); setErrors(previous => { const next = { ...previous }; delete next.accountType; return next; }); }} />{option}
              </label>)}
            </div>
            {errors.accountType && <p className="forms-error">{errors.accountType}</p>}
          </div>
          <div className="payment-preview-grid">
            {field("accountHolder", "Account holder", { hint: "Synthetic test name only; no real bank details.", maxLength: 65 })}
            {field("accountNumber", "Account number", { hint: `Test accepts ${PAYMENT_TEST_ACCOUNT} only. Never enter a real account number.`, inputMode: "numeric", maxLength: 12 })}
            {field("routingNumber", "Routing number", { hint: `Test accepts ${PAYMENT_TEST_ROUTING} only. Never enter a real routing number.`, inputMode: "numeric", maxLength: 9 })}
            {field("bankName", "Bank name", { maxLength: 65 })}
            {field("bankAddress", "Bank address", { maxLength: 65 })}
            {field("bankCityStateZip", "City, state, ZIP", { maxLength: 65 })}
          </div>
        </fieldset>
      </>}

      <fieldset className="forms-section">
        <legend><span>03</span>Authorized signer</legend>
        <div className="payment-preview-grid">
          {field("signerName", "Printed name", { maxLength: 45, autoComplete: "name" })}
          {field("title", "Title", { maxLength: 30 })}
          <div className="payment-preview-field is-wide">
            <label className="payment-preview-label" htmlFor="payment-signature">Typed signature</label>
            <input id="payment-signature" name="signature" className={`forms-control payment-preview-input payment-preview-signature-input${errors.signature ? " is-invalid" : ""}`} value={values.signature} onChange={event => updateField("signature", event.target.value)} maxLength={45} disabled={submitting} aria-invalid={!!errors.signature} aria-describedby={errors.signature ? "payment-signature-error" : undefined} />
            <div className="payment-preview-signature" aria-label="Signature preview">{values.signature}</div>
            {errors.signature && <p className="forms-error" id="payment-signature-error">{errors.signature}</p>}
            <p className="forms-hint">For this synthetic test, the signature must match the printed name.</p>
          </div>
          {field("date", "Date", { type: "date" })}
        </div>
        <label className={`forms-consent${errors.consent ? " has-error" : ""}`}>
          <input type="checkbox" checked={consent} disabled={submitting} onChange={event => { setConsent(event.target.checked); setErrors(previous => { const next = { ...previous }; delete next.consent; return next; }); }} />
          <span>I consent to use of this typed name as an electronic signature for this synthetic test only.</span>
        </label>
        {errors.consent && <p className="forms-error">{errors.consent}</p>}
      </fieldset>

      {method === "ach" && <section className="forms-section payment-preview-authorization">
        <h3>ACH authorization · test text only</h3>
        <p>{PAYMENT_PREVIEW_ACH_AUTHORIZATION}</p>
        <p>{PAYMENT_PREVIEW_ACH_TERMINATION}</p>
        <p className="payment-preview-no-authorization">No debit is initiated. This test does not create an ACH authorization.</p>
      </section>}

      <fieldset className="forms-section">
        <legend><span>04</span>Financial terms</legend>
        <p className="payment-preview-terms">{PAYMENT_PREVIEW_TERMS}</p>
        <div className="payment-preview-choices payment-preview-terms-choices" role="radiogroup" aria-label="Payment terms">
          {["Due On Receipt", "NET 15", "NET 30"].map(choice => <label key={choice} className={`payment-preview-choice${terms === choice ? " is-selected" : ""}`}>
            <input type="radio" name="terms" value={choice} checked={terms === choice} disabled={submitting} onChange={() => { setTerms(choice); setErrors(previous => { const next = { ...previous }; delete next.terms; return next; }); }} />{choice}
          </label>)}
        </div>
        {errors.terms && <p className="forms-error">{errors.terms}</p>}
        <p className="payment-preview-fee">{PAYMENT_PREVIEW_FEE_NOTE}</p>
        {method === "credit-card" && <>
          <label className={`forms-consent payment-preview-fee-check${errors.feeAcknowledgment ? " has-error" : ""}`}>
            <input type="checkbox" checked={feeAcknowledgment} disabled={submitting} onChange={event => { setFeeAcknowledgment(event.target.checked); setErrors(previous => { const next = { ...previous }; delete next.feeAcknowledgment; return next; }); }} />
            <span>I acknowledge the credit-card fee note for this test.</span>
          </label>
          {errors.feeAcknowledgment && <p className="forms-error">{errors.feeAcknowledgment}</p>}
        </>}
      </fieldset>

      <fieldset className="payment-preview-test-checks">
        <legend>Test-only confirmations</legend>
        <label className={`forms-consent${errors.syntheticOnly ? " has-error" : ""}`}>
          <input type="checkbox" checked={syntheticOnly} disabled={submitting} onChange={event => { setSyntheticOnly(event.target.checked); setErrors(previous => { const next = { ...previous }; delete next.syntheticOnly; return next; }); }} />
          <span>I confirm that every value supplied is synthetic test information only, not real financial or personal data.</span>
        </label>
        {errors.syntheticOnly && <p className="forms-error">{errors.syntheticOnly}</p>}
        {emailEligible ? <label className={`forms-consent${errors.emailCopy ? " has-error" : ""}`}>
          <input type="checkbox" checked={emailCopy} disabled={submitting} onChange={event => { setEmailCopy(event.target.checked); setErrors(previous => { const next = { ...previous }; delete next.emailCopy; return next; }); }} />
          <span>Send synthetic test PDF to support@premierpharma.com <small>(optional; unchecked by default)</small></span>
        </label> : <p className="forms-hint payment-preview-email-note">{config?.emailAvailable === false ? "Test email is not available right now. Sign in as an administrator with test email access to email a synthetic PDF." : config?.adminEmailAllowed === false ? "To email a synthetic test PDF, sign in as an administrator with test email access." : "Test email can only be requested by an eligible administrator."}</p>}
        {errors.emailCopy && <p className="forms-error">{errors.emailCopy}</p>}
      </fieldset>

      {message && <div className="forms-alert forms-alert-error" role="alert">{message}</div>}
      <div className="payment-preview-actions">
        <div className="payment-preview-pdf-links">
          <a className="forms-button forms-button-secondary" href="/api/forms/payment-information/preview.pdf?method=credit-card" target="_blank" rel="noreferrer">Credit Card PDF preview</a>
          <a className="forms-button forms-button-secondary" href="/api/forms/payment-information/preview.pdf?method=ach" target="_blank" rel="noreferrer">ACH PDF preview</a>
        </div>
        <button type="submit" className="forms-button" disabled={submitting || !!configError || !config}>
          {submitting ? "Generating test PDF…" : "Generate test PDF"}
        </button>
      </div>
      <p className="payment-preview-disclaimer">This synthetic form does not process payment, create a real authorization, or create a portal submission or admin repository record. Test information is not retained and no retention countdown exists.</p>
    </form>}
    <footer className="forms-footer"><span>Premier Pharmaceuticals</span><span>Pre-production test form</span></footer>
  </main>;
}
